import RexUIPlugin from 'phaser3-rex-plugins/templates/ui/ui-plugin.js';
import Dialog from 'phaser3-rex-plugins/templates/ui/dialog/Dialog';
import UIScene from './UIScene';
import { Character } from '../Character';
import { Portal } from '../Portal';
import { Preferences } from '@capacitor/preferences';
import MainScene from 'src/app/Scenes/MainScene';
import { GlobalService } from 'src/app/services/global.service';
import { showToast } from '../Utilities';

export class DialogInformer extends Phaser.Physics.Matter.Sprite {
  name: string;
  dialogContent: string;
  url: string;
  token?: string;
}

export class CustomDialog {
  rexUI: RexUIPlugin;
  scene: UIScene;
  dialog: Dialog;
  timedEvent: Phaser.Time.TimerEvent;
  eventCounter: number = 0;
  dialogSpeed: number = 0.1;
  informer: Character | Portal;
  dialogContentText: string[];
  token?: string;
  isActive: boolean = true;
  dialogMessage: string = '';
  public readonly scale: number = GlobalService.device === 'phone' ? 0.5 : 1;

  constructor(informer: Character | Portal, dialogMessage: string = '', token?: string) {
    this.scene = informer.scene.scene.manager.getScene('UIScene') as UIScene;
    this.rexUI = this.scene.rexUI;
    this.informer = informer;
    this.token = token;
    this.dialogMessage = dialogMessage;
    this.displayContent();
  }

  createDialog(statement: string){
    // Layout and populate the dialog
    this.dialog = this.rexUI.add
      .dialog({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
        width: 500*this.scale,

        background: this.rexUI.add.roundRectangle(
          0,
          0,
          100,
          100,
          20,
          0x1565c0,
          0.5
        ),

        title: this.createLabel(this.scene, this.informer.name),

        content: this.createLabel(this.scene, statement),

        choices: this.informer.url ? [
          this.createLabel(this.scene, 'Learn More'),
        ] : [],

        actions: [
          this.createLabel(this.scene, 'Close'),
        ],

        space: {
          left: 20,
          right: 20,
          top: -20,
          bottom: -20,

          title: 25,
          titleLeft: 30,
          content: 25,
          choices: 25,

          toolbarItem: 5,
          choice: 15,
          action: 15,
        },

        expand: {
          title: false,
        },

        align: {
          title: 'center',
          actions: 'right', // 'center'|'left'|'right'
        },

        click: {
          mode: 'pointerdown',
        },
      })
      .layout()
      .popUp(750);

    // Bind buttons to actions
    this.dialog
      .on(
        'button.click',
        async function (button, groupName, index, pointer, event) {
          // Close modal when close is clicked
          if (button.text == 'Close') {
            this.close();
          } else if (button.text == 'Learn More') {
            // Try window.open first
            const opened = window.open(this.informer.url, '_blank');
            if (!opened) {
              // Fallback: show a visible link for the user to tap
              // alert('Popup blocked. Please tap the link below to continue:\n' + this.informer.url);
              await showToast({message: 'Popup blocked. Tap to open link.', url: this.informer.url, duration: 5000});;
              // Optionally, display a clickable link in your UI
            }
          } 
        },
        this
      )
      .on('button.over', function (button, groupName, index, pointer, event) {
        button.getElement('background').setStrokeStyle(1, 0xffffff);
      })
      .on('button.out', function (button, groupName, index, pointer, event) {
        button.getElement('background').setStrokeStyle();
      });

    this.scene.scale.on('resize', () => {
      this.scene.time.delayedCall(150, () => {
        this.dialog.x = window.innerWidth / 2;
        this.dialog.y = window.innerHeight / 2;
      }, [], this.scene);
    });
  }

  selectStatement(): string{

    if(this.dialogMessage.length > 0){
      return this.dialogMessage;
    }

    try{
      const dialogObject = JSON.parse(this.informer.dialogContent || '{}');

      // loop over all tokens from local storage
      // await MainScene.getTokens();
      const tokens = MainScene.tokens;
      for(let i=tokens.length-1; i>=0; i--){

        // If a matching dialog exists
        // select the most recent one
        // and return it
        if(dialogObject[tokens[i]]){
          const statements: string[] = dialogObject[tokens[i]];
          const randIdx = Math.ceil(Math.random()*statements.length-1);
          return statements[randIdx];
        }
      }

      // Otherwise, next try to randomly select one of the default statements
      if(dialogObject['default']){
        const defaults = dialogObject['default']
        const randIdx = Math.ceil(Math.random()*defaults.length-1);
        return defaults[randIdx];
      }
    }

    // Finally, default to displaying the raw dialogContent
    catch{
      return this.informer.dialogContent;
    }
  }

  close() {
    if (this.isActive) {
      if (this.timedEvent) this.timedEvent.remove();
      if (this.dialog) this.dialog.scaleDownDestroy(200);
      if(this.token){
        if(!MainScene.tokens.includes(this.token)){
          console.log(`adding token: ${this.token}`);
          MainScene.tokens.push(this.token);
          Preferences.set({key: "tokens", value: JSON.stringify(MainScene.tokens)});
          // Store clientModifiedAt in Preferences
          Preferences.set({
            key: 'clientModifiedAt',
            value: new Date().toISOString(),
          });
        }
      }
      this.isActive = false;
    }
  }

  formatStatement(statement: string): string{
    // Add line breaks if the statement is too long
    const maxLineLength = 40;
    let words = statement.split(' ');
    let formattedStatement = '';
    let currentLine = '';

    words.forEach(word => {
      if ((currentLine + word).length > maxLineLength) {
        formattedStatement += currentLine.trim() + '\n';
        currentLine = word + ' ';
      } else {
        currentLine += word + ' ';
      }
    });
    formattedStatement += currentLine.trim();
    return formattedStatement;
  }

  // ALlows words to be displayed sequentially instead of all at once
  displayContent() {
    if (this.timedEvent) this.timedEvent.remove();
    let statement = this.formatStatement(this.selectStatement());
    this.createDialog(statement);
    let dialogText = this.dialog.childrenMap.content as Phaser.GameObjects.Text;
    if(this.dialogMessage.length === 0){
      this.dialogContentText = statement.split(' ');
      dialogText.setText('');
      dialogText.setVisible(true);
      this.timedEvent = this.scene.scene.scene.time.addEvent({
        delay: 250,
        callback: this._animateContent,
        callbackScope: this,
        loop: true,
      });
    }
  }

  _animateContent() {
    let dialogText = this.dialog.childrenMap.content as Phaser.GameObjects.Text;
    this.eventCounter++;
    dialogText.setText(
      dialogText.text + ' ' + this.dialogContentText[this.eventCounter - 1]
    );
    if (this.eventCounter >= this.dialogContentText.length) {
      this.timedEvent.remove();
    }
  }

  createLabel(scene: UIScene, text: string) {
    return scene.rexUI.add.label({
      width: 40, // Minimum width of round-rectangle
      height: 40, // Minimum height of round-rectangle

      background: scene.rexUI.add.roundRectangle(0, 0, 100, 40, 20, 0x5e92f3),

      text: scene.add
        .text(0, 0, text, {
          fontSize: GlobalService.device === 'phone' ? '12px' : '12px',
        })
        .setVisible(true),

      space: {
        left: 10,
        right: 10,
        top: 10,
        bottom: 10,
      },
    });
  }
}
